import React, { useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Ticket, Upload, X, Loader2 } from 'lucide-react';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';

const TYPES = [
  { value: 'issue', label: 'Issue — something is broken' },
  { value: 'improvement', label: 'Improvement — a better way to do this' },
  { value: 'question', label: 'Question — I need to understand something' },
];

const EMPTY = { type: 'issue', subject: '', description: '', school_id: '' };

/** Small, quiet launcher for the Report Issue modal, pinned bottom-left. */
export default function ReportIssueButton({ schools, sidebar = false }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const isAdmin = user?.role === 'admin';

  if (!user || !['coordinator', 'admin'].includes(user.role)) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.description.trim()) {
      toast.error('Subject and description are both needed');
      return;
    }
    setSaving(true);
    try {
      const body = new FormData();
      body.append('type', form.type);
      body.append('subject', form.subject.trim());
      body.append('description', form.description.trim());
      body.append('page_url', window.location.href);
      if (isAdmin && form.school_id) body.append('school_id', form.school_id);
      if (file) body.append('screenshot', file);
      const res = await api.post('/tickets', body, { headers: { 'Content-Type': 'multipart/form-data' } });
      const number = res.data.school_ticket_number || res.data.ticket_number;
      toast.success(`Ticket ${number} raised — we will look into it`);
      window.dispatchEvent(new CustomEvent('ticket-created'));
      setForm(EMPTY);
      setFile(null);
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not raise this ticket');
    }
    setSaving(false);
  };

  return (
    <>
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => setOpen(true)}
              className={`fixed z-40 bottom-4 ${sidebar ? 'left-4 md:left-[15rem]' : 'left-4'} h-11 w-11 sm:h-9 sm:w-9 flex items-center justify-center rounded-full bg-white/90 backdrop-blur border border-gray-200 text-[#9CA3AF] shadow-sm hover:text-[#F97D6A] hover:border-[#F97D6A]/40 transition-colors duration-200`}
              data-testid="report-issue-fab"
              aria-label="Report Issue"
            >
              <Ticket className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" data-testid="report-issue-tooltip">Report Issue</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Dialog open={open} onOpenChange={(v) => { if (!saving) setOpen(v); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="report-issue-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Report an issue</DialogTitle>
            <DialogDescription>
              Tell us what happened on this page. We capture the page, your account and the date
              automatically, so you only need the detail.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs text-gray-500">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="mt-1 rounded-xl" data-testid="ticket-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value} data-testid={`ticket-type-${t.value}`}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {isAdmin && (
              <div>
                <Label className="text-xs text-gray-500">School (optional)</Label>
                <Select value={form.school_id || 'none'} onValueChange={(v) => setForm({ ...form, school_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="mt-1 rounded-xl" data-testid="ticket-school-select">
                    <SelectValue placeholder="No school" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No school — platform wide</SelectItem>
                    {(schools || []).map(s => (
                      <SelectItem key={s.school_id} value={s.school_id}>{s.school_name} ({s.school_id})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="text-xs text-gray-500">Subject</Label>
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                     placeholder="One line that names the problem" maxLength={140}
                     className="mt-1 rounded-xl" data-testid="ticket-subject-input" />
            </div>

            <div>
              <Label className="text-xs text-gray-500">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                        rows={5} placeholder="What were you doing, what you expected and what happened instead"
                        className="mt-1 rounded-xl" data-testid="ticket-description-input" />
            </div>

            <div>
              <Label className="text-xs text-gray-500">Screenshot (optional)</Label>
              {file ? (
                <div className="mt-1 flex items-center gap-2 text-sm text-[#1F2937] bg-[#F8F9FA] rounded-xl px-3 py-2" data-testid="ticket-screenshot-chosen">
                  <span className="truncate flex-1">{file.name}</span>
                  <button type="button" onClick={() => setFile(null)} className="text-gray-400 hover:text-red-500" data-testid="ticket-screenshot-clear">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <label className="mt-1 flex items-center gap-2 text-sm text-[#6B7280] border border-dashed border-gray-300 rounded-xl px-3 py-2.5 cursor-pointer hover:border-[#F97D6A] hover:text-[#F97D6A] transition-colors">
                  <Upload className="h-4 w-4" />
                  <span>Attach one image</span>
                  <input type="file" accept="image/*" className="hidden"
                         onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="ticket-screenshot-input" />
                </label>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={saving}
                      className="rounded-xl" data-testid="ticket-cancel-btn">Cancel</Button>
              <Button type="submit" disabled={saving}
                      className="rounded-xl bg-[#F97D6A] hover:bg-[#e86a57]" data-testid="ticket-submit-btn">
                {saving ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Sending</> : 'Send ticket'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
